import React from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Users, Home, Building, Phone, ChevronRight, ArrowLeft, Star, CheckCircle, Clock, Truck } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import LocalAreaServices from '@/components/LocalAreaServices';
import AreaSEOContent from '@/components/AreaSEOContent';

const AreaDetailPage = () => {
  const { areaSlug } = useParams();
  const { language, t } = useLanguage();

  const areaData: { [key: string]: any } = {
    'the-woodlands': {
      name: 'The Woodlands',
      state: 'TX',
      population: '114,000+',
      founded: '1974',
      description: language === 'en' 
        ? 'The Woodlands is a master-planned community known for its commitment to preserving the natural forest canopy while creating beautiful, sustainable landscapes.'
        : 'The Woodlands es una comunidad planificada conocida por su compromiso de preservar el dosel forestal natural mientras crea paisajes hermosos y sostenibles.',
      
      demographics: {
        medianIncome: '$95,000+',
        homeownership: '75%',
        avgHomeValue: '$450,000+'
      },
      
      features: language === 'en'
        ? [
            'Over 150 parks and green spaces',
            'Extensive hike and bike trail system',
            'Corporate headquarters and business parks',
            'Golf courses and country clubs',
            'Luxury residential communities',
            'Town centers and shopping districts'
          ]
        : [
            'Más de 150 parques y espacios verdes',
            'Extenso sistema de senderos para caminar y ciclismo',
            'Sedes corporativas y parques empresariales',
            'Campos de golf y clubes de campo',
            'Comunidades residenciales de lujo',
            'Centros urbanos y distritos comerciales'
          ],
          
      services: [
        {
          title: language === 'en' ? 'Sod Installation' : 'Instalación de Césped',
          description: language === 'en' 
            ? 'Professional sod installation tailored to The Woodlands homes and HOAs'
            : 'Instalación profesional de césped adaptada a las casas y HOAs de The Woodlands',
          price: language === 'en' ? 'Quote on request' : 'Cotización bajo solicitud'
        },
        {
          title: language === 'en' ? 'Fresh Grass Delivery' : 'Entrega de Pasto Fresco',
          description: language === 'en'
            ? 'Quality St. Augustine, Bermuda, and Zoysia delivered to your property'
            : 'Pasto St. Augustine, Bermuda y Zoysia de calidad entregado a tu propiedad',
          price: language === 'en' ? 'Quote on request' : 'Cotización bajo solicitud'
        }
      ],
    },
    'cypress': {
      name: 'Cypress',
      state: 'TX',
      population: '170,000+',
      founded: '1840s',
      description: language === 'en' 
        ? 'Cypress is a rapidly growing area with modern developments, family-friendly communities, and diverse landscaping opportunities from residential subdivisions to major commercial centers.'
        : 'Cypress es un área de rápido crecimiento con desarrollos modernos, comunidades familiares y diversas oportunidades de paisajismo desde subdivisiones residenciales hasta grandes centros comerciales.',
      
      demographics: {
        medianIncome: '$70,000+',
        homeownership: '78%',
        avgHomeValue: '$320,000+'
      },
      
      features: language === 'en'
        ? [
            'Master-planned communities like Fairfield and Bridgeland',
            'Major shopping centers and retail developments',
            'New construction neighborhoods',
            'Quality school districts',
            'Close proximity to major employers',
            'Mix of suburban and rural properties'
          ]
        : [
            'Comunidades planificadas como Fairfield y Bridgeland',
            'Grandes centros comerciales y desarrollos de retail',
            'Vecindarios de nueva construcción',
            'Distritos escolares de calidad',
            'Proximidad cercana a grandes empleadores',
            'Mezcla de propiedades suburbanas y rurales'
          ],
          
      services: [
        {
          title: language === 'en' ? 'Sod Installation' : 'Instalación de Césped',
          description: language === 'en' 
            ? 'St. Augustine, Bermuda, and Zoysia for new construction and established homes'
            : 'St. Augustine, Bermuda y Zoysia para construcción nueva y casas establecidas',
          price: language === 'en' ? 'Quote on request' : 'Cotización bajo solicitud'
        },
        {
          title: language === 'en' ? 'Fresh Grass Delivery' : 'Entrega de Pasto Fresco',
          description: language === 'en'
            ? 'Fast delivery of premium sod to your Cypress property'
            : 'Entrega rápida de césped premium a tu propiedad en Cypress',
          price: language === 'en' ? 'Quote on request' : 'Cotización bajo solicitud'
        }
      ],
    },
    'magnolia': {
      name: 'Magnolia',
      state: 'TX',
      population: '2,000+',
      founded: '1890s',
      description: language === 'en' 
        ? 'Magnolia offers the perfect blend of small-town charm and modern growth, with families seeking beautiful landscapes for their growing community and large residential properties.'
        : 'Magnolia ofrece la mezcla perfecta de encanto de pueblo pequeño y crecimiento moderno, con familias que buscan paisajes hermosos para su comunidad en crecimiento y grandes propiedades residenciales.',
      
      demographics: {
        medianIncome: '$75,000+',
        homeownership: '85%',
        avgHomeValue: '$350,000+'
      },
      
      features: language === 'en'
        ? [
            'Historic downtown district with local businesses',
            'Family-friendly neighborhoods with large lots',
            'Growing school district serving young families',
            'Rural atmosphere with urban convenience',
            'Close to major employment centers in The Woodlands',
            'Strong sense of community and local pride'
          ]
        : [
            'Distrito histórico del centro con negocios locales',
            'Vecindarios familiares con lotes grandes',
            'Distrito escolar en crecimiento sirviendo familias jóvenes',
            'Atmósfera rural con conveniencia urbana',
            'Cerca de centros de empleo principales en The Woodlands',
            'Fuerte sentido de comunidad y orgullo local'
          ],
          
      services: [
        {
          title: language === 'en' ? 'Fresh Grass Delivery' : 'Entrega de Pasto Fresco',
          description: language === 'en' 
            ? 'Quality sod varieties perfect for large properties and new homes'
            : 'Variedades de césped de calidad perfectas para propiedades grandes y casas nuevas',
          price: language === 'en' ? 'Quote on request' : 'Cotización bajo solicitud'
        },
        {
          title: language === 'en' ? 'Sod Installation' : 'Instalación de Césped',
          description: language === 'en'
            ? 'Professional installation for rural and residential properties'
            : 'Instalación profesional para propiedades rurales y residenciales',
          price: language === 'en' ? 'Quote on request' : 'Cotización bajo solicitud'
        }
      ]
    },
    'conroe': {
      name: 'Conroe',
      state: 'TX',
      population: '95,000+',
      founded: '1881',
      description: language === 'en' 
        ? 'Conroe combines historic charm with modern growth, offering diverse landscaping opportunities from lakefront properties to downtown commercial spaces and established residential neighborhoods.'
        : 'Conroe combina encanto histórico con crecimiento moderno, ofreciendo diversas oportunidades de paisajismo desde propiedades frente al lago hasta espacios comerciales del centro y vecindarios residenciales establecidos.',
      
      demographics: {
        medianIncome: '$65,000+',
        homeownership: '68%',
        avgHomeValue: '$280,000+'
      },
      
      features: language === 'en'
        ? [
            'Historic downtown district with revitalization projects',
            'Lake Conroe waterfront properties and resorts',
            'Established neighborhoods with quality lawns',
            'Growing arts and cultural scene',
            'Montgomery County seat with government facilities',
            'Mix of historic and modern commercial developments'
          ]
        : [
            'Distrito histórico del centro con proyectos de revitalización',
            'Propiedades frente al agua y resorts del Lago Conroe',
            'Vecindarios establecidos con árboles maduros',
            'Escena artística y cultural en crecimiento',
            'Sede del Condado de Montgomery con instalaciones gubernamentales',
            'Mezcla de desarrollos comerciales históricos y modernos'
          ],
          
      services: [
        {
          title: language === 'en' ? 'Grass for Historic Properties' : 'Pasto para Propiedades Históricas',
          description: language === 'en' 
            ? 'Quality sod varieties suitable for Conroe\'s heritage homes'
            : 'Variedades de césped de calidad adecuadas para las casas patrimoniales de Conroe',
          price: language === 'en' ? 'Quote on request' : 'Cotización bajo solicitud'
        },
        {
          title: language === 'en' ? 'Sod Installation' : 'Instalación de Césped',
          description: language === 'en'
            ? 'Professional sod installation for residential and commercial properties'
            : 'Instalación profesional de césped para propiedades residenciales y comerciales',
          price: language === 'en' ? 'Quote on request' : 'Cotización bajo solicitud'
        }
      ]
    },
    'spring': {
      name: 'Spring',
      state: 'TX',
      population: '85,000+',
      founded: '1840s',
      description: language === 'en' 
        ? 'Spring is a vibrant community with diverse neighborhoods, from established family areas to modern developments, offering exceptional landscaping opportunities for every type of property.'
        : 'Spring es una comunidad vibrante con vecindarios diversos, desde áreas familiares establecidas hasta desarrollos modernos, ofreciendo oportunidades excepcionales de paisajismo para todo tipo de propiedad.',
      
      demographics: {
        medianIncome: '$72,000+',
        homeownership: '76%',
        avgHomeValue: '$295,000+'
      },
      
      features: language === 'en'
        ? [
            'Klein and Spring school districts',
            'Mix of established and new neighborhoods',
            'Convenient access to major highways',
            'Growing commercial and retail centers',
            'Family-oriented community amenities',
            'Diverse housing options and lot sizes'
          ]
        : [
            'Distritos escolares Klein y Spring',
            'Mezcla de vecindarios establecidos y nuevos',
            'Acceso conveniente a autopistas principales',
            'Centros comerciales y de retail en crecimiento',
            'Amenidades comunitarias orientadas a familias',
            'Opciones diversas de vivienda y tamaños de lotes'
          ],
          
      services: [
        {
          title: language === 'en' ? 'Family Home Sod Installation' : 'Instalación de Césped para Casas Familiares',
          description: language === 'en' 
            ? 'Complete sod installation for family-friendly properties'
            : 'Instalación completa de césped para propiedades familiares',
          price: language === 'en' ? 'From $9,000' : 'Desde $9,000'
        },
        {
          title: language === 'en' ? 'Neighborhood Grass Services' : 'Servicios de Césped para Vecindarios',
          description: language === 'en'
            ? 'Community-wide grass installation and maintenance services'
            : 'Servicios de instalación y mantenimiento de césped para toda la comunidad',
          price: language === 'en' ? 'Quote on request' : 'Cotización bajo solicitud'
        },
        {
          title: language === 'en' ? 'Commercial Sod Installation' : 'Instalación de Césped Comercial',
          description: language === 'en'
            ? 'Professional sod installation for shopping centers and office complexes'
            : 'Instalación profesional de césped para centros comerciales y complejos de oficinas',
          price: language === 'en' ? 'Contract pricing' : 'Precios de contrato'
        }
      ],
      
      testimonials: [
        {
          name: 'Amanda Williams',
          location: 'Klein Area',
          text: language === 'en'
            ? 'The Grass Depot installed beautiful sod for our family. Our children love playing on the soft, healthy grass!'
            : 'The Grass Depot instaló hermoso césped para nuestra familia. ¡A nuestros hijos les encanta jugar en el césped suave y saludable!',
          rating: 5,
          project: language === 'en' ? 'Family lawn installation' : 'Instalación de césped familiar'
        },
        {
          name: 'Carlos Mendoza',
          location: 'Spring Gardens',
          text: language === 'en'
            ? 'Professional service and excellent results. They completely transformed our front yard with beautiful sod and native plants.'
            : 'Servicio profesional y excelentes resultados. Transformaron completamente nuestro jardín frontal con césped hermoso y plantas nativas.',
          rating: 5,
          project: language === 'en' ? 'Front yard renovation' : 'Renovación del jardín frontal'
        }
      ],
      
      recentProjects: [
        {
          title: language === 'en' ? 'Spring Creek Village' : 'Aldea Spring Creek',
          type: language === 'en' ? 'Residential' : 'Residencial',
          size: '50 homes',
          completion: '2024'
        },
        {
          title: language === 'en' ? 'Klein Business District' : 'Distrito Empresarial Klein',
          type: language === 'en' ? 'Commercial' : 'Comercial',
          size: '20 acres',
          completion: '2023'
        }
      ]
    },
    'tomball': {
      name: 'Tomball',
      state: 'TX',
      population: '45,000+',
      founded: '1906',
      description: language === 'en' 
        ? 'Tomball combines small-town heritage with modern growth, featuring historic downtown charm alongside new residential developments that require thoughtful, quality landscaping solutions.'
        : 'Tomball combina herencia de pueblo pequeño con crecimiento moderno, presentando encanto histórico del centro junto con nuevos desarrollos residenciales que requieren soluciones de paisajismo reflexivas y de calidad.',
      
      demographics: {
        medianIncome: '$68,000+',
        homeownership: '79%',
        avgHomeValue: '$275,000+'
      },
      
      features: language === 'en'
        ? [
            'Historic downtown with annual festivals',
            'Tomball ISD with excellent schools',
            'Close to major employment centers',
            'New residential developments',
            'Community parks and recreation facilities',
            'Growing arts and culture scene'
          ]
        : [
            'Centro histórico con festivales anuales',
            'Tomball ISD con excelentes escuelas',
            'Cerca de centros de empleo principales',
            'Nuevos desarrollos residenciales',
            'Parques comunitarios e instalaciones recreativas',
            'Escena artística y cultural en crecimiento'
          ],
          
      services: [
        {
          title: language === 'en' ? 'Historic Home Landscapes' : 'Paisajes de Casas Históricas',
          description: language === 'en' 
            ? 'Specialized landscaping that complements historic architecture'
            : 'Paisajismo especializado que complementa la arquitectura histórica',
          price: language === 'en' ? 'From $10,000' : 'Desde $10,000'
        },
        {
          title: language === 'en' ? 'New Development Packages' : 'Paquetes de Nuevos Desarrollos',
          description: language === 'en'
            ? 'Complete landscaping solutions for new construction neighborhoods'
            : 'Soluciones completas de paisajismo para vecindarios de nueva construcción',
          price: language === 'en' ? 'From $8,500' : 'Desde $8,500'
        },
        {
          title: language === 'en' ? 'Community Grass Services' : 'Servicios de Césped Comunitarios',
          description: language === 'en'
            ? 'Professional grass installation and maintenance for community spaces'
            : 'Instalación y mantenimiento profesional de césped para espacios comunitarios',
          price: language === 'en' ? 'Quote on request' : 'Cotización bajo solicitud'
        }
      ],
      
      testimonials: [
        {
          name: 'Patricia Davis',
          location: 'Downtown Tomball',
          text: language === 'en'
            ? 'They perfectly captured the historic charm of our neighborhood while creating a modern, functional landscape for our family.'
            : 'Capturaron perfectamente el encanto histórico de nuestro vecindario mientras creaban un paisaje moderno y funcional para nuestra familia.',
          rating: 5,
          project: language === 'en' ? 'Historic home landscape' : 'Paisaje de casa histórica'
        },
        {
          name: 'Mark Thompson',
          location: 'Rosehill Reserve',
          text: language === 'en'
            ? 'Excellent work on our new home. They delivered exactly what they promised, on time and within budget.'
            : 'Excelente trabajo en nuestra nueva casa. Entregaron exactamente lo que prometieron, a tiempo y dentro del presupuesto.',
          rating: 5,
          project: language === 'en' ? 'New construction landscape' : 'Paisaje de nueva construcción'
        }
      ],
      
      recentProjects: [
        {
          title: language === 'en' ? 'Tomball Town Square' : 'Plaza de Tomball',
          type: language === 'en' ? 'Municipal' : 'Municipal',
          size: '3 acres',
          completion: '2024'
        },
        {
          title: language === 'en' ? 'Rosehill Reserve Development' : 'Desarrollo Rosehill Reserve',
          type: language === 'en' ? 'Residential' : 'Residencial',
          size: '75 homes',
          completion: '2023'
        }
      ]
    },
    'montgomery': {
      name: 'Montgomery',
      state: 'TX',
      population: '15,000+',
      founded: '1837',
      description: language === 'en' 
        ? 'Montgomery is a historic town with deep Texas roots, offering unique landscaping opportunities that honor its heritage while meeting modern needs for both residential and commercial properties.'
        : 'Montgomery es un pueblo histórico con raíces profundas de Texas, ofreciendo oportunidades únicas de paisajismo que honran su herencia mientras satisfacen necesidades modernas para propiedades tanto residenciales como comerciales.',
      
      demographics: {
        medianIncome: '$75,000+',
        homeownership: '82%',
        avgHomeValue: '$320,000+'
      },
      
      features: language === 'en'
        ? [
            'Historic downtown district on the National Register',
            'Close to Lake Conroe recreational opportunities',
            'Antique shops and local businesses',
            'Rural residential properties with large lots',
            'Strong community heritage and traditions',
            'Growing tourism and cultural activities'
          ]
        : [
            'Distrito histórico del centro en el Registro Nacional',
            'Cerca de oportunidades recreativas del Lago Conroe',
            'Tiendas de antigüedades y negocios locales',
            'Propiedades residenciales rurales con lotes grandes',
            'Fuerte herencia comunitaria y tradiciones',
            'Turismo y actividades culturales en crecimiento'
          ],
          
      services: [
        {
          title: language === 'en' ? 'Heritage Property Landscaping' : 'Paisajismo de Propiedades Patrimoniales',
          description: language === 'en' 
            ? 'Specialized care for historic and heritage properties'
            : 'Cuidado especializado para propiedades históricas y patrimoniales',
          price: language === 'en' ? 'From $12,000' : 'Desde $12,000'
        },
        {
          title: language === 'en' ? 'Rural Estate Development' : 'Desarrollo de Fincas Rurales',
          description: language === 'en'
            ? 'Comprehensive landscaping for large rural properties'
            : 'Paisajismo integral para grandes propiedades rurales',
          price: language === 'en' ? 'From $15,000' : 'Desde $15,000'
        },
        {
          title: language === 'en' ? 'Tourism Business Landscapes' : 'Paisajes de Negocios Turísticos',
          description: language === 'en'
            ? 'Commercial landscaping for antique shops and tourism businesses'
            : 'Paisajismo comercial para tiendas de antigüedades y negocios turísticos',
          price: language === 'en' ? 'Quote on request' : 'Cotización bajo solicitud'
        }
      ],
    },
    'pinehurst': {
      name: 'Pinehurst',
      state: 'TX',
      population: '8,000+',
      founded: '1960s',
        description: language === 'en' 
        ? 'Pinehurst is a charming residential community known for its well-established neighborhoods and family-friendly atmosphere, offering excellent opportunities for quality grass installation that enhances property values.'
        : 'Pinehurst es una comunidad residencial encantadora conocida por sus vecindarios bien establecidos y atmósfera familiar, ofreciendo excelentes oportunidades para instalación de césped de calidad que mejora los valores de las propiedades.',
      
      demographics: {
        medianIncome: '$78,000+',
        homeownership: '84%',
        avgHomeValue: '$285,000+'
      },
      
      features: language === 'en'
        ? [
            'Quiet residential neighborhoods with established lawns',
            'Well-maintained community amenities',
            'Close proximity to The Woodlands and major employers',
            'Family-oriented community with local schools',
            'Mix of established and updated homes',
            'Strong neighborhood associations and community pride',
            'Excellent soil conditions for grass installation'
          ]
        : [
            'Vecindarios residenciales tranquilos con céspedes establecidos',
            'Amenidades comunitarias bien mantenidas',
            'Proximidad cercana a The Woodlands y empleadores principales',
            'Comunidad orientada a familias con escuelas locales',
            'Mezcla de casas establecidas y renovadas',
            'Asociaciones vecinales fuertes y orgullo comunitario',
            'Excelentes condiciones de suelo para instalación de césped'
          ],
          
      services: [
        {
          title: language === 'en' ? 'Premium Sod Installation' : 'Instalación de Césped Premium',
          description: language === 'en' 
            ? 'Quality grass installation for established neighborhoods'
            : 'Instalación de césped de calidad para vecindarios establecidos',
          price: language === 'en' ? 'From $2,500' : 'Desde $2,500'
        },
        {
          title: language === 'en' ? 'Property Value Enhancement' : 'Mejora del Valor de la Propiedad',
          description: language === 'en'
            ? 'Strategic grass installation designed to maximize curb appeal and property value'
            : 'Instalación estratégica de césped diseñada para maximizar el atractivo exterior y valor de la propiedad',
          price: language === 'en' ? 'From $3,500' : 'Desde $3,500'
        },
        {
          title: language === 'en' ? 'HOA Standards Compliance' : 'Cumplimiento de Estándares de HOA',
          description: language === 'en'
            ? 'Grass installation that meets HOA requirements while reflecting personal style'
            : 'Instalación de césped que cumple con requisitos de HOA mientras refleja estilo personal',
          price: language === 'en' ? 'From $2,000' : 'Desde $2,000'
        }
      ],
      
      testimonials: [
        {
          name: 'Susan Mitchell',
          location: 'Pinehurst Forest',
          text: language === 'en'
            ? 'They installed beautiful St. Augustine sod that perfectly complements our neighborhood\'s character. Our property value has definitely increased!'
            : 'Instalaron hermoso césped San Agustín que complementa perfectamente el carácter de nuestro vecindario. ¡El valor de nuestra propiedad definitivamente ha aumentado!',
          rating: 5,
          project: language === 'en' ? 'Complete lawn installation' : 'Instalación completa de césped'
        },
        {
          name: 'James Rodriguez',
          location: 'Pinehurst Village',
          text: language === 'en'
            ? 'Professional team that delivered fresh, quality sod and installed it perfectly. They provided excellent guidance on watering and care for the first few weeks.'
            : 'Equipo profesional que entregó césped fresco y de calidad y lo instaló perfectamente. Proporcionaron excelente orientación sobre riego y cuidado durante las primeras semanas.',
          rating: 5,
          project: language === 'en' ? 'Front yard sod installation' : 'Instalación de césped del patio delantero'
        }
      ],
      
      recentProjects: [
        {
          title: language === 'en' ? 'Pinehurst Community Center' : 'Centro Comunitario Pinehurst',
          type: language === 'en' ? 'Community' : 'Comunitario',
          size: '2 acres',
          completion: '2024'
        },
        {
          title: language === 'en' ? 'Forest Glen Subdivision' : 'Subdivisión Forest Glen',
          type: language === 'en' ? 'Residential' : 'Residencial',
          size: '30 homes',
          completion: '2023'
        }
      ]
    }
  };

  const currentArea = areaData[areaSlug || ''] || areaData['the-woodlands'];

  // Enhanced SEO structured data for local business
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `The Grass Depot LLC - ${currentArea.name} Landscaping Services`,
    "image": "https://thegrassdepotllc.com/grass-depot-logo.png",
    "telephone": "(936) 320-0763",
    "email": "info@thegrassdepotllc.com",
    "url": `https://thegrassdepotllc.com/${language}/areas/${areaSlug}`,
    "description": currentArea.description,
    "priceRange": "$$$",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": currentArea.name,
      "addressRegion": "TX",
      "addressCountry": "US",
      "postalCode": "77380"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "30.1588",
      "longitude": "-95.4613"
    },
    "areaServed": {
      "@type": "City",
      "name": currentArea.name,
      "sameAs": `https://en.wikipedia.org/wiki/${currentArea.name.replace(' ', '_')}_Texas`
    },
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "30.1588",
        "longitude": "-95.4613"
      },
      "geoRadius": "50000"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `Landscaping Services in ${currentArea.name}`,
      "itemListElement": (currentArea.services || []).map((service: any, index: number) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.title,
          "description": service.description
        },
        "price": service.price,
        "priceCurrency": "USD"
      }))
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "150",
      "bestRating": "5"
    },
    "review": (currentArea.testimonials || []).map((testimonial: any) => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": testimonial.name
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": testimonial.rating,
        "bestRating": "5"
      },
      "reviewBody": testimonial.text
    })),
    "openingHours": ["Mo-Fr 07:00-18:00", "Sa 08:00-16:00"],
    "sameAs": [
      "https://www.facebook.com/TheGrassDepotLLC",
      "https://www.instagram.com/thegrassdepot"
    ]
  };

  const seoTitle = language === 'en' 
    ? `${currentArea.name} Landscaping Services - Sod, Rock, Mulch Delivery`
    : `Servicios de Paisajismo en ${currentArea.name} - Entrega de Césped, Roca, Mantillo`;
  
  const seoDescription = language === 'en'
    ? `Professional grass delivery and installation services in ${currentArea.name}, Texas. Premium sod installation, bulk material delivery. Serving ${currentArea.population} residents.`
    : `Servicios profesionales de entrega e instalación de césped en ${currentArea.name}, Texas. Instalación de césped premium, entrega de materiales a granel. Sirviendo a ${currentArea.population} residentes.`;

  const seoKeywords = language === 'en'
    ? `${currentArea.name} grass delivery, ${currentArea.name} sod installation, ${currentArea.name} lawn care, ${currentArea.name} mulch delivery, Texas grass installation, Montgomery County sod`
    : `paisajismo ${currentArea.name}, instalación césped ${currentArea.name}, cuidado césped ${currentArea.name}, entrega mantillo ${currentArea.name}, paisajismo Texas, paisajismo Condado Montgomery`;

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={`/${language}/areas/${areaSlug}`}
        structuredData={structuredData}
        language={language}
        breadcrumbs={[
          { name: language === 'en' ? 'Home' : 'Inicio', url: `/${language}` },
          { name: language === 'en' ? 'Service Areas' : 'Áreas de Servicio', url: `/${language}/areas` },
          { name: currentArea.name, url: `/${language}/areas/${areaSlug}` },
        ]}
      />
      <Header />
      
      <main>
      {/* Breadcrumb */}
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center text-sm text-muted-foreground">
          <Link to={`/${language}`} className="hover:text-primary transition-colors">
            {language === 'en' ? 'Home' : 'Inicio'}
          </Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <Link to={`/${language}/areas`} className="hover:text-primary transition-colors">
            {language === 'en' ? 'Areas We Serve' : 'Áreas que Servimos'}
          </Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <span className="text-foreground">
            {currentArea.name}, {currentArea.state}
          </span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl">
            <Link to={`/${language}/areas`} className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Back to All Areas' : 'Volver a Todas las Áreas'}
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <h1 className="text-4xl md:text-6xl font-bold">
                {currentArea.name}
              </h1>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-3 py-1">
                <Users className="h-4 w-4 mr-1" />
                {currentArea.population}
              </Badge>
            </div>
            <p className="text-xl md:text-2xl text-white/90 mb-8">
              {currentArea.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
                <MapPin className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Local Expert' : 'Experto Local'}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
                <Star className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Top Rated Service' : 'Servicio Mejor Calificado'}
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Area Stats */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div className="bg-background p-6 rounded-lg shadow-card">
              <div className="text-2xl font-bold text-primary mb-2">{currentArea.population}</div>
              <div className="text-sm text-muted-foreground">
                {language === 'en' ? 'Population' : 'Población'}
              </div>
            </div>
            <div className="bg-background p-6 rounded-lg shadow-card">
              <div className="text-2xl font-bold text-primary mb-2">{currentArea.founded}</div>
              <div className="text-sm text-muted-foreground">
                {language === 'en' ? 'Established' : 'Establecido'}
              </div>
            </div>
            <div className="bg-background p-6 rounded-lg shadow-card">
              <div className="text-2xl font-bold text-primary mb-2">{currentArea.demographics.medianIncome}</div>
              <div className="text-sm text-muted-foreground">
                {language === 'en' ? 'Median Income' : 'Ingreso Promedio'}
              </div>
            </div>
            <div className="bg-background p-6 rounded-lg shadow-card">
              <div className="text-2xl font-bold text-primary mb-2">{currentArea.demographics.homeownership}</div>
              <div className="text-sm text-muted-foreground">
                {language === 'en' ? 'Homeownership' : 'Propietarios'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Area Features */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'What Makes' : 'Qué Hace a'} {currentArea.name} {language === 'en' ? 'Special' : 'Especial'}
            </h2>
            <p className="text-lg text-muted-foreground">
              {language === 'en'
                ? 'Understanding the unique characteristics that shape our landscaping approach'
                : 'Entendiendo las características únicas que moldean nuestro enfoque de paisajismo'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(currentArea.features || []).map((feature: string, index: number) => (
              <div key={index} className="flex items-start p-4 bg-background rounded-lg shadow-card">
                <CheckCircle className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Services */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Our' : 'Nuestros'} {currentArea.name} {language === 'en' ? 'Services' : 'Servicios'}
            </h2>
            <p className="text-lg text-muted-foreground">
              {language === 'en'
                ? 'Specialized landscaping solutions tailored to your community'
                : 'Soluciones de paisajismo especializadas adaptadas a tu comunidad'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {(currentArea.services || []).map((service: any, index: number) => (
              <Card key={index} className="hover:shadow-glow transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-foreground">
                    {service.title}
                  </CardTitle>
                  <div className="text-lg font-semibold text-primary">
                    {service.price}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    {service.description}
                  </p>
                  <Button variant="outline" className="w-full">
                    {language === 'en' ? 'Get Quote' : 'Obtener Cotización'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'en' 
              ? `Ready to Transform Your ${currentArea.name} Property?`
              : `¿Listo para Transformar tu Propiedad en ${currentArea.name}?`
            }
          </h2>
          <p className="text-lg text-secondary-foreground/80 mb-8 max-w-2xl mx-auto">
            {language === 'en'
              ? `Get a free consultation and quote specifically tailored to ${currentArea.name} properties and regulations.`
              : `Obtén una consulta gratuita y cotización específicamente adaptada a las propiedades y regulaciones de ${currentArea.name}.`
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
              <Button variant="hero" size="lg">
                {language === 'en' ? 'Get Free Quote' : 'Obtener Cotización Gratis'}
              </Button>
            </Link>
            <a href="tel:9363200763">
              <Button variant="outline" size="lg" className="border-secondary-foreground/20 hover:bg-secondary-foreground/10">
                <Phone className="mr-2 h-5 w-5" />
                {language === 'en' ? 'Call Local Expert' : 'Llamar Experto Local'}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* SEO-Enhanced Area Content */}
      <AreaSEOContent 
        areaName={currentArea.name}
        areaSlug={areaSlug || ''}
        population={currentArea.population}
        demographics={currentArea.demographics}
      />
      </main>
      
      <Footer />
    </div>
  );
};

export default AreaDetailPage;